import { GridSortModel } from '@components/DataTable';

export type SortOption = {
  key: string;
  label: string;
  sortModel: GridSortModel;
};

/**
 * Canonical sort option groups for library media, used by both the desktop
 * sort on the search line (`LibraryToolbar`) and the phone's view options sheet
 * (`MobileViewOptions`). Keep additions/edits here so both stay in sync.
 *
 * The publishedAt group is conditional — consumers should filter it out
 * when no media has publishedAt data.
 */
export const SORT_GROUPS: SortOption[][] = [
  [
    {
      key: 'filename-asc',
      label: 'Title, A to Z',
      sortModel: [{ field: 'filename', sort: 'asc' }],
    },
    {
      key: 'filename-desc',
      label: 'Title, Z to A',
      sortModel: [{ field: 'filename', sort: 'desc' }],
    },
  ],
  [
    {
      key: 'updatedAt-desc',
      label: 'Last modified',
      sortModel: [{ field: 'updatedAt', sort: 'desc' }],
    },
    {
      key: 'updatedAt-asc',
      label: 'First modified',
      sortModel: [{ field: 'updatedAt', sort: 'asc' }],
    },
  ],
  [
    {
      key: 'createdAt-desc',
      label: 'Last added',
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
    },
    {
      key: 'createdAt-asc',
      label: 'First added',
      sortModel: [{ field: 'createdAt', sort: 'asc' }],
    },
  ],
  [
    {
      key: 'publishedAt-desc',
      label: 'Last published',
      sortModel: [{ field: 'publishedAt', sort: 'desc' }],
    },
    {
      key: 'publishedAt-asc',
      label: 'First published',
      sortModel: [{ field: 'publishedAt', sort: 'asc' }],
    },
  ],
  [
    { key: 'duration-desc', label: 'Longest', sortModel: [{ field: 'duration', sort: 'desc' }] },
    { key: 'duration-asc', label: 'Shortest', sortModel: [{ field: 'duration', sort: 'asc' }] },
  ],
];

/** Flat lookup: sort key → GridSortModel */
export const SORT_OPTIONS: Record<string, GridSortModel> = Object.fromEntries(
  SORT_GROUPS.flat().map((opt) => [opt.key, opt.sortModel]),
);

/** Flat lookup: sort key → display label */
export const SORT_OPTION_LABELS: Record<string, string> = {
  ...Object.fromEntries(SORT_GROUPS.flat().map((opt) => [opt.key, opt.label])),
  custom: 'Custom',
};

/** Fields available in the grid sort dropdown */
export const GRID_SORTABLE_FIELDS = new Set(
  SORT_GROUPS.flat().map((opt) => opt.sortModel[0].field),
);

/** Reverse lookup: get dropdown option key from current sortModel */
export function getSortOptionKey(sortModel: GridSortModel): string {
  if (sortModel.length === 0) return 'createdAt-desc';
  const { field, sort } = sortModel[0];
  const key = `${field}-${sort}`;
  if (!GRID_SORTABLE_FIELDS.has(field) || !SORT_OPTIONS[key]) {
    return 'custom';
  }
  return key;
}

/** Filter out the publishedAt group when no media has publishedAt data */
export function getVisibleSortGroups(hasPublishedAt: boolean): SortOption[][] {
  return SORT_GROUPS.filter((group) => hasPublishedAt || group[0].key !== 'publishedAt-desc');
}
